import React, { useRef, useState } from 'react';
import { CheckCircle, Hourglass, XCircle, Upload, FileDown, DollarSign } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';
import QuoteDetails from './QuoteComponents/QuoteDetails';
import { WorkItem as WorkItemType, LineItem, Quote } from '../../models/workItem';
import propertyService from '../../services/propertyService';
import InspectionQuoteDetails from './InspectionQuoteComponents/InspectionQuoteDetails';
import { generateWorkItemPDF } from '../../services/pdfGeneratorService';

interface WorkItemProps {
  workItem: WorkItemType;
  propertyId: string;
  quote?: Quote;
  onAnalyze: (workItemId: string) => void;
  address: string;
}

const WorkItem: React.FC<WorkItemProps> = ({ workItem, propertyId, quote, onAnalyze, address }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const calculateCostsFromAnalysis = (lineItems: LineItem[] | undefined) => {
    if (!lineItems?.length) {
      return {
        lowEstimate: workItem.estimatedCostLow,
        highEstimate: workItem.estimatedCostHigh,
      };
    }

    const costs = lineItems.reduce(
      (acc, item) => {
        const laborCostLow = item.analysis.subTasks.reduce((subAcc, subTask) => {
          return (
            subAcc +
            subTask.options.reduce(
              (optAcc, option) =>
                optAcc +
                (option.expectedLaborHoursLow * option.laborCostLowPerHour || 0),
              0
            )
          );
        }, 0);

        const laborCostHigh = item.analysis.subTasks.reduce((subAcc, subTask) => {
          return (
            subAcc +
            subTask.options.reduce(
              (optAcc, option) =>
                optAcc +
                (option.expectedLaborHoursHigh * option.laborCostHighPerHour || 0),
              0
            )
          );
        }, 0);

        const materialCostLow = item.analysis.subTasks.reduce((subAcc, subTask) => {
          return (
            subAcc +
            subTask.options.reduce(
              (optAcc, option) =>
                optAcc +
                option.materials.reduce(
                  (matAcc, material) =>
                    matAcc + material.lowCostPerUnit * material.quantityLow,
                  0
                ),
              0
            )
          );
        }, 0);

        const materialCostHigh = item.analysis.subTasks.reduce((subAcc, subTask) => {
          return (
            subAcc +
            subTask.options.reduce(
              (optAcc, option) =>
                optAcc +
                option.materials.reduce(
                  (matAcc, material) =>
                    matAcc + material.highCostPerUnit * material.quantityHigh,
                  0
                ),
              0
            )
          );
        }, 0);

        return {
          lowEstimate: Math.ceil(acc.lowEstimate + laborCostLow + materialCostLow),
          highEstimate: Math.ceil(acc.highEstimate + laborCostHigh + materialCostHigh),
        };
      },
      { lowEstimate: 0, highEstimate: 0 }
    );

    return costs;
  };

  const getEstimates = () => {
    if (workItem.quote) {
      return calculateCostsFromAnalysis(workItem.quote.analysis.lineItems);
    }
    else if (workItem.inspectionQuote) {
      return calculateCostsFromAnalysis(workItem.inspectionQuote.analysis.lineItems);
    } else {
      return {lowEstimate: 0, highEstimate: 0}
    }
  };

  const { lowEstimate, highEstimate } = getEstimates();

  const handleAnalyzeClick = async () => {
    setIsLoading(true);
    try {
      await onAnalyze(workItem.id);
    } catch (error) {
      console.error(`Error analyzing work item ${workItem.id}:`, error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setQuoteLoading(true);
      try {
        const uploadedQuote = await propertyService.createQuoteAnalysisRecord(
          propertyId,
          workItem.id,
          file
        );
        console.log('Quote uploaded and attached successfully:', uploadedQuote);
      } catch (error) {
        console.error(`Error uploading quote for work item ${workItem.id}:`, error);
      } finally {
        setQuoteLoading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    }
  };

  const getStatusDisplay = (status: string) => {
    switch (status) {
      case 'COMPLETE':
        return (
          <Badge variant="outline" className="flex items-center gap-1 bg-green-50 text-green-700 border-green-200">
            <CheckCircle className="h-3.5 w-3.5" />
            <span className="font-semibold">Complete</span>
          </Badge>
        );
      case 'IN PROGRESS':
        return (
          <Badge variant="outline" className="flex items-center gap-1 bg-blue-50 text-blue-700 border-blue-200">
            <Hourglass className="h-3.5 w-3.5" />
            <span className="font-semibold">In Progress</span>
          </Badge>
        );
      case 'NOT STARTED':
        return (
          <Badge variant="outline" className="flex items-center gap-1 bg-slate-50 text-slate-700 border-slate-200">
            <XCircle className="h-3.5 w-3.5" />
            <span className="font-semibold">Not Started</span>
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="flex items-center gap-1 bg-red-50 text-red-700 border-red-200">
            <XCircle className="h-3.5 w-3.5" />
            <span className="font-semibold">Unknown</span>
          </Badge>
        );
    }
  };

  return (
    <li className="list-none mb-4 p-4 bg-white">
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        onChange={handleFileChange}
      />

      {/* Header Section - Just buttons */}
      <div className="flex justify-end mb-6">
        {/* Buttons */}
        <div className="flex gap-2">
          <Button 
            variant="default" 
            onClick={handleUploadClick} 
            disabled={quoteLoading}
            className="flex items-center gap-2"
          >
            <Upload className="h-4 w-4" />
            {quoteLoading ? 'Uploading...' : 'Upload Quote'}
          </Button>
          
          <Button
            variant="outline"
            onClick={() => generateWorkItemPDF(workItem, address)}
            className="flex items-center gap-2"
          >
            <FileDown className="h-4 w-4" />
            Download PDF
          </Button>
        </div>
      </div>

      {/* Inspection Quote Details - without any wrapper */}
      {workItem?.inspectionQuote && (
        <div className="mb-4">
          <InspectionQuoteDetails quote={workItem?.inspectionQuote} propertyId={propertyId} />
        </div>
      )}

      {/* Quote Details */}
      {workItem?.quote && <QuoteDetails quote={workItem?.quote} />}
    </li>
  );
};

export default WorkItem;
